import React from "react";
import { useTheme } from "react-jss";
import classnames from "classnames";
import { IconProp } from "@fortawesome/fontawesome-svg-core";
import { FontAwesomeIcon } from "./FontAwesome";
import useStyles from "./Button.styles";
import { isNullOrEmpty } from "../../utils";

interface Props {
    className?: string;
    disabled?: boolean;
    active?: boolean;
    hidden?: boolean;
    icon?: IconProp;
    iconPosition?: "start" | "end";
    iconOnHover?: boolean;
    size?: "small" | "large" | "full" | "flex" | number;
    text?: string;
    displayText?: boolean;
    displayIcon?: boolean;
    onClick(): void;
    imageUrl?: string;
}

interface IconProps {
    active: boolean;
    disabled?: boolean;
    iconOnHover: boolean;
    icon: IconProp;
    iconPosition?: "start" | "end";
    size?: "small" | "large" | "full" | "flex" | number;
    textAndIcon: any;
    imageUrl?: string;
}

function getSizeClassName(classes, size) {
    if (typeof size === "number") {
        return "";
    }
    switch (size) {
        case "small":
            return classes.buttonSmall;
        case "full":
            return classes.buttonFull;
        case "flex":
            return classes.buttonFlex;
        default:
            return classes.buttonLarge;
    }
}

function ButtonIcon(props: IconProps) {
    const {
        size,
        active,
        disabled,
        icon,
        iconPosition,
        textAndIcon,
        iconOnHover,
        imageUrl,
    } = props;

    const theme = useTheme();
    const classes = useStyles({ theme, size, iconPosition, textAndIcon, iconOnHover });

    return (
        <div
            className={
                !imageUrl &&
                classnames(
                    classes.buttonIconContainer,
                    textAndIcon && iconOnHover && classes.buttonIconAnimated,
                    textAndIcon && active && iconOnHover && classes.buttonIconAnimatedHover,
                )
            }
        >
            {!imageUrl && (
                <FontAwesomeIcon
                    fixedWidth
                    className={classnames(
                        classes.buttonIcon,
                        disabled && classes.buttonIconDisabled,
                    )}
                    icon={icon}
                />
            )}
            {imageUrl && <img src={imageUrl} className={classes.pointer} />}
        </div>
    );
}

export default function Button(props: Props) {
    const {
        size,
        className,
        disabled,
        onClick,
        active,
        hidden,
        text,
        icon,
        iconPosition,
        iconOnHover,
        displayText,
        displayIcon,
        imageUrl,
        ...otherProps
    } = props;

    const touchEvent = "ontouchstart" in window;

    const renderText = displayText !== false && !isNullOrEmpty(text);
    const renderIcon = displayIcon !== false && icon != null;
    const textAndIcon = renderText && renderIcon;

    const position = iconPosition || "end";

    const theme = useTheme();
    const classes = useStyles({ theme, iconPosition: position });

    const [_active, setActive] = React.useState(false);

    const isActive = active || _active;

    return (
        <button
            type="button"
            aria-label={text}
            onFocus={() => setActive(true)}
            onBlur={() => setActive(false)}
            onMouseOver={() => setActive(true)}
            onMouseOut={() => setActive(false)}
            onClick={() => {
                if (!touchEvent) {
                    if (disabled) {
                        return;
                    }
                    if (onClick) {
                        onClick();
                    }
                }
            }}
            onTouchStart={() => {
                if (disabled) {
                    return;
                }
                if (onClick) {
                    onClick();
                }
            }}
            onKeyPress={(event) => {
                if (disabled) {
                    return;
                }
                if (event.key === " " || event.key === "Enter") {
                    if (onClick) {
                        onClick();
                    }
                    event.preventDefault();
                }
            }}
            className={classnames(
                className,
                !imageUrl && classes.button,
                !imageUrl && classes.buttonBorderNone,
                !imageUrl && getSizeClassName(classes, size),
                isActive && !disabled && !imageUrl && classes.buttonActive,
                disabled && classes.buttonDisabled,
                hidden && classes.buttonHidden,
            )}
            {...otherProps}
        >
            {position === "start" && renderIcon && (
                <ButtonIcon
                    size={size}
                    icon={icon}
                    iconPosition={position}
                    iconOnHover={iconOnHover}
                    active={isActive}
                    textAndIcon={textAndIcon}
                    disabled={disabled}
                    imageUrl={imageUrl}
                />
            )}
            {renderText && !imageUrl && (
                <div
                    className={classnames(
                        classes.buttonTextContainer,
                        textAndIcon && iconOnHover && classes.buttonTextAnimated,
                        textAndIcon && isActive && iconOnHover && classes.buttonTextAnimatedHover,
                    )}
                >
                    {text}
                </div>
            )}
            {position === "end" && renderIcon && (
                <ButtonIcon
                    size={size}
                    icon={icon}
                    active={isActive}
                    iconPosition={position}
                    textAndIcon={textAndIcon}
                    disabled={disabled}
                    iconOnHover={iconOnHover}
                    imageUrl={imageUrl}
                />
            )}
        </button>
    );
}
